import type { CompanySearchResult } from '@dash-lite/sdk';
import { Activity, Building2, MapPin } from 'lucide-react';
import { useCompanyPreview } from '@/hooks/use-company-preview';

/**
 * Aperçu affiché à côté du formulaire de paiement.
 *
 * Il répond à la seule question que se pose le visiteur à cet instant :
 * « est-ce que je paie pour la bonne entreprise ? ». Tout ce qu'il montre
 * vient de l'appel **gratuit et public** `GET /companies/search` — la fiche
 * payante n'est pas demandée, et ne pourrait de toute façon pas l'être : sans
 * droit d'accès, l'API répond `401` ou `403`.
 *
 * Le parti pris de présentation compte autant que le code. Les rubriques que
 * l'accès ouvrira sont **nommées, sans valeur ni faux-semblant** : pas de
 * lignes floutées, pas de vignette de document, pas de contenu qui aurait
 * l'air d'être là et qu'on retiendrait. Ce qui n'est pas affiché n'a pas été
 * envoyé au navigateur, et l'écran ne prétend pas le contraire.
 */
export function CompanyPreview({
    registrationNumber,
    initial = null,
}: {
    registrationNumber: string | null;
    /** Résultat de recherche transporté depuis la liste, s'il existe. */
    initial?: CompanySearchResult | null;
}) {
    const { company } = useCompanyPreview(registrationNumber, initial);

    console.log("CompanyPreview company:", company);
    return (
        <div className="space-y-4">
    <div className="order-1 mt-15 lg:mt-0 lg:h-full">
        <div
            className="relative h-full overflow-hidden rounded-[16px] border border-border shadow-[0_8px_26px_rgba(2,6,23,.06)]"
            style={{
                background: "linear-gradient(135deg, #1D3557 0%, #3E92CC 100%)",
            }}
        >
            {/* Pattern de points */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(transparent_1px,currentColor_1px)] [background-size:10px_10px] text-sidebar-foreground opacity-[.08]" />

            <div className="relative flex h-full flex-col items-center justify-center gap-8 p-6 pb-40 lg:p-10 lg:pb-48">
                {/* Image Preview */}
                <img
                    className="w-[min(420px,92%)] -rotate-2 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,.35)] transition-transform duration-300 hover:scale-105 hover:rotate-0"
                    src={'/images/specimen-document.png'}
                    alt="Aperçu document"
                    loading="lazy"
                />

                {/* Badge de validation */}
                 <div
    aria-hidden="true"
    className="absolute top-[8%] right-[8%] isolate grid h-[70px] w-[70px] place-items-center rounded-full bg-white shadow-lg lg:right-[12%] lg:h-[88px] lg:w-[88px]"
>
    <svg
        viewBox="0 0 100 100"
        className="relative z-[1] h-[60px] w-[60px] lg:h-[80px] lg:w-[80px]"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
    >
        {/* Définition des ombres pour l'effet 3D subtil */}
        <defs>
            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
        </defs>

        {/* 1. Anneau de fond (Gris très clair pour la structure) */}
        <circle cx="50" cy="50" r="40" strokeWidth="8" stroke="#E2E8F0" />

        {/* 2. Segment Principal (Bleu marine, comme le dégradé de la carte) */}
        <circle
            cx="50"
            cy="50"
            r="40"
            strokeWidth="8"
            strokeLinecap="round"
            stroke="#1D3557"
            className="transition-all duration-500 ease-out"
            strokeDasharray="180 251"
            transform="rotate(-90 50 50)"
        />

        {/* 3. Segment Accent (Corail/Rose) */}
        <circle
            cx="50"
            cy="50"
            r="40"
            strokeWidth="8"
            strokeLinecap="round"
            stroke="#E63946"
            className="drop-shadow-sm"
            strokeDasharray="50 251"
            strokeDashoffset="-190"
            transform="rotate(-90 50 50)"
        />

        {/* 4. Checkmark (Dessiné au trait, moderne) */}
        <path
            d="M32 50 L45 63 L68 38"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            stroke="#1D3557"
        />
    </svg>
</div>
            </div>

            {/* Infos entreprise — carte flottante en bas de l'image */}
            <div className="absolute inset-x-5 bottom-5 space-y-3 rounded-2xl bg-white/15 p-5 text-sm text-white shadow-[0_8px_24px_rgba(0,0,0,.25)] backdrop-blur-md lg:inset-x-8 lg:bottom-8 lg:p-6 lg:text-base">
                {/* Ligne : Nom de la société */}
                <div className="flex items-start gap-3">
                    <Building2 className="mt-0.5 h-5 w-5 shrink-0 opacity-90" aria-hidden />
                    <div>
                        <span className="block font-bold">{company?.name || ""}</span>
                        <span className="text-xs opacity-80">{company?.registration_number || "000"}</span>
                    </div>
                </div>

                {/* Ligne : Adresse */}
                <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 opacity-90" aria-hidden />
                    <span className="opacity-90">{company?.city || ""}</span>
                </div>

                {/* Ligne : Activité */}
                <div className="flex items-start gap-3">
                    <Activity className="mt-0.5 h-5 w-5 shrink-0 opacity-90" aria-hidden />
                    <span
                        className="line-clamp-1 opacity-90"
                        title={company?.legal_form || ""}
                    >
                        {company?.legal_form || ""}
                    </span>
                </div>
            </div>
        </div>
    </div>
</div>
        
    );
}

